/*  src/views/.../WaafiPayTab.jsx  */
import CardBody from '@/components/ui/cards/CardBody';
import CardHeader from '@/components/ui/cards/CardHeader';
import { Input } from '@/components/ui/inputs/input';
import { InputSelect } from '@/components/ui/inputs/input-select';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import validationErrors from '@/lib/validationErrors';
import {
  getPaymentBySlugService,
  updatePaymentService,
} from '@/services/PaymentService';
import PermissionCheck from '@/components/common/PermissionCheck';

const waafiModeList = [
  { value: '1', label: 'Live' },
  { value: '0', label: 'Sandbox' },
];
const waafiStatus = [
  { value: 1, label: 'Enable' },
  { value: 2, label: 'Disable' },
];

const WaafiPayTab = () => {
  const {
    register,
    setValue,
    getValues,
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const dispatch = useDispatch();
  const [isSubmitting, setSubmitting] = useState(false);

  /* ── load saved settings ─────────────────────── */
  useEffect(() => {
    dispatch(getPaymentBySlugService('waafi'))
      .then((res) => {
        const gw = res?.data?.data?.[0];
        if (!gw) return;

        const opts = gw.gateway_options ?? [];
        reset({
          waafi_id: gw._id,
          status: gw.status,
          waafi_api_key   : opts.find((o) => o.option === 'waafi_api_key')?.value ?? '',
          waafi_merchant_id: opts.find((o) => o.option === 'waafi_merchant_id')?.value ?? '',
          waafi_user_id   : opts.find((o) => o.option === 'waafi_user_id')?.value ?? '',
          waafi_mode      : opts.find((o) => o.option === 'waafi_mode')?.value ?? '',
        });
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message));
  }, []);

  /* ── submit ───────────────────────────────────── */
  const onSubmit = (data) => {
    setSubmitting(true);

    const payload = {
      id: data.waafi_id,
      status: data.status,              // already a number
      options: [
        { option: 'waafi_api_key',    value: data.waafi_api_key },
        { option: 'waafi_merchant_id',value: data.waafi_merchant_id },
        { option: 'waafi_user_id',    value: data.waafi_user_id },
        { option: 'waafi_mode',       value: data.waafi_mode }, // ← primitive!
      ],
    };

    dispatch(updatePaymentService(payload))
      .finally(() => setSubmitting(false))
      .catch(({ message, errorData }) => validationErrors(errorData, message));
  };

  /* ── render ───────────────────────────────────── */
  return (
    <>
      <CardHeader isBorder>
        <h3 className="db-card-title">Waafi Pays</h3>
      </CardHeader>

      <CardBody>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input type="hidden" {...register('waafi_id')} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* API KEY */}
            <Controller
              control={control}
              name="waafi_api_key"
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  {...field}
                  labelText="API Key"
                  labelStyleClassName="db-field-title required"
                  errorType={errors.waafi_api_key?.type}
                  size="sm"
                />
              )}
            />

            {/* MERCHANT ID */}
            <Controller
              control={control}
              name="waafi_merchant_id"
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  {...field}
                  labelText="Merchant ID"
                  labelStyleClassName="db-field-title required"
                  errorType={errors.waafi_merchant_id?.type}
                  size="sm"
                />
              )}
            />

            {/* USER ID */}
            <Controller
              control={control}
              name="waafi_user_id"
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  {...field}
                  labelText="User ID"
                  labelStyleClassName="db-field-title required"
                  errorType={errors.waafi_user_id?.type}
                  size="sm"
                />
              )}
            />

            {/* MODE */}
            <Controller
              control={control}
              name="waafi_mode"
              rules={{ required: true }}
              render={({ field }) => (
                <InputSelect
                  {...field}
                  id="waafi_mode"
                  labelText="Mode"
                  labelStyleClassName="db-field-title required"
                  options={waafiModeList}
                  value={waafiModeList.find((o) => o.value === field.value) || null}
                  onChange={(opt) => field.onChange(opt.value)}   // ← store primitive
                  errorType={errors.waafi_mode?.type}
                />
              )}
            />

            {/* STATUS */}
            <Controller
              control={control}
              name="status"
              rules={{ required: true }}
              render={({ field }) => (
                <InputSelect
                  {...field}
                  id="status"
                  labelText="Status"
                  labelStyleClassName="db-field-title required"
                  options={waafiStatus}
                  value={waafiStatus.find((o) => o.value === field.value) || null}
                  onChange={(opt) => field.onChange(opt.value)}
                  errorType={errors.status?.type}
                />
              )}
            />

            {/* SAVE BUTTON */}
            <PermissionCheck permsission="update_settings">
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="db-btn text-white bg-primary"
                >
                  <i className="lab lab-save" />
                  <span>Save</span>
                  {isSubmitting && (
                    <svg
                      className="animate-spin h-5 w-5 ml-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </PermissionCheck>
          </div>
        </form>
      </CardBody>
    </>
  );
};

export default WaafiPayTab;
